import { useState, useEffect, useRef, useCallback } from 'react'
import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin from '@fullcalendar/interaction'
import listPlugin from '@fullcalendar/list'
import { operationAPI } from '../services/api'
import { useAuth } from '../contexts/AuthContext'
import PageHeader from '../components/PageHeader'

// Pastel color palette for country-based coloring (lighter backgrounds)
const pastelColors = [
  { bg: '#fff4e6', border: '#fb923c' }, // Very light orange
  { bg: '#ffe4e6', border: '#f87171' }, // Very light red/pink
  { bg: '#e6f9f0', border: '#4ade80' }, // Very light green
  { bg: '#e6f2ff', border: '#60a5fa' }, // Very light blue
  { bg: '#f5ebff', border: '#a78bfa' }, // Very light purple
  { bg: '#fff9e6', border: '#fbbf24' }, // Very light yellow/amber
  { bg: '#f0edff', border: '#8b5cf6' }, // Very light indigo
  { bg: '#fef0f8', border: '#f472b6' }, // Very light pink
  { bg: '#e6fafe', border: '#22d3ee' }, // Very light cyan
  { bg: '#e6f9f2', border: '#10b981' }, // Very light emerald
  { bg: '#fffbf0', border: '#f59e0b' }, // Very light amber
  { bg: '#f0f2ff', border: '#6366f1' }, // Very light indigo
  { bg: '#fef0f5', border: '#ec4899' }, // Very light rose
  { bg: '#e6f2ff', border: '#3b82f6' }, // Very light sky blue
  { bg: '#f5f0ff', border: '#9333ea' }, // Very light violet
  { bg: '#fffef0', border: '#eab308' }, // Very light yellow
  { bg: '#e6f5fe', border: '#0ea5e9' }, // Very light light blue
  { bg: '#f0fdf5', border: '#22c55e' }, // Very light lime
  { bg: '#fff5f6', border: '#f43f5e' }, // Very light rose
  { bg: '#e6f7ff', border: '#0284c7' }, // Very light cyan
]

// Generate a consistent color for a country name
const getCountryColor = (countryName) => {
  if (!countryName || countryName.trim() === '') {
    // Default color for operations without country
    return pastelColors[3] // Default to blue
  }

  // Normalize country name (case-insensitive, trimmed)
  const normalized = countryName.trim().toLowerCase()
  
  // Simple hash function to convert country name to a number
  let hash = 0
  for (let i = 0; i < normalized.length; i++) {
    const char = normalized.charCodeAt(i)
    hash = ((hash << 5) - hash) + char
    hash = hash & hash // Convert to 32-bit integer
  }
  
  // Use absolute value and modulo to get index
  const index = Math.abs(hash) % pastelColors.length
  return pastelColors[index]
}

// Parse operation description to extract structured data
const parseDescription = (description) => {
  if (!description) return {}
  
  const lines = description.split('\n')
  const data = {
    client: '',
    port: '',
    country: '',
    surveyor: '',
    notes: '',
  }
  
  lines.forEach(line => {
    if (line.startsWith('Client:')) {
      data.client = line.replace('Client:', '').trim()
    } else if (line.startsWith('Port:')) {
      const portLine = line.replace('Port:', '').trim()
      const parts = portLine.split(',')
      data.port = parts[0]?.trim() || ''
      data.country = parts[1]?.trim() || ''
    } else if (line.startsWith('Surveyor:')) {
      data.surveyor = line.replace('Surveyor:', '').trim()
    } else if (line.startsWith('Notes:')) {
      data.notes = line.replace('Notes:', '').trim()
    } else if (line && !line.startsWith('Client:') && !line.startsWith('Port:') && !line.startsWith('Surveyor:')) {
      // Additional notes lines
      if (data.notes) {
        data.notes += ' ' + line.trim()
      }
    }
  })
  
  return data
}

// Operation Detail Modal Component
const OperationModal = ({ operation, isOpen, onClose }) => {
  // Safely get isClientuser with fallback
  let isClientuser = false
  try {
    const auth = useAuth()
    isClientuser = auth?.isClientuser || false
  } catch (error) {
    // If useAuth fails, default to false (show surveyor)
    isClientuser = false
  }
  
  if (!isOpen || !operation) return null

  const parsed = parseDescription(operation.extendedProps?.description || '')
  // Use country-based color if available, otherwise fallback to default
  const countryColor = getCountryColor(parsed.country)
  const bgColor = countryColor.bg
  const borderColor = countryColor.border

  // Format date for display (European timezone-safe)
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A'
    
    // Parse date string as European time to ensure consistency
    let date
    if (typeof dateStr === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
      // Parse date components directly (timezone-neutral for date-only values)
      const [year, month, day] = dateStr.split('-').map(Number)
      date = new Date(year, month - 1, day)
    } else if (dateStr instanceof Date) {
      date = dateStr
    } else {
      date = new Date(dateStr)
    }
    
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      timeZone: 'Europe/London'  // Display in European timezone
    })
  }

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-30 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col border-l-4"
        style={{ borderLeftColor: borderColor }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div 
          className="p-6 rounded-t-3xl"
          style={{ 
            backgroundColor: bgColor,
          }}
        >
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h2 className="text-2xl font-bold mb-2 text-kbl-gray-800">{operation.title}</h2>
              <div className="flex items-center gap-4 text-sm text-kbl-gray-700">
                <span className="flex items-center gap-1.5 bg-white/60 px-3 py-1.5 rounded-full">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  {formatDate(operation.start)}
                </span>
                {operation.end && operation.end !== operation.start && (
                  <span className="flex items-center gap-1.5 bg-white/60 px-3 py-1.5 rounded-full">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    {formatDate(operation.end)}
                  </span>
                )}
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-kbl-gray-600 hover:text-kbl-gray-800 hover:bg-white/60 transition-all p-2 rounded-full"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Modal Body */}
        <div className="p-6 space-y-4 overflow-y-auto flex-1">
          {/* Survey Reference */}
          {operation.extendedProps?.survey_ref && (
            <div className="flex items-center gap-3 p-4 rounded-xl border-l-4" style={{ 
              backgroundColor: `${bgColor}80`,
              borderLeftColor: borderColor 
            }}>
              <div className="flex-shrink-0">
                <svg className="w-5 h-5" style={{ color: borderColor }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <p className="text-sm text-kbl-gray-600 font-medium">Survey Reference</p>
                <p className="text-lg font-semibold text-kbl-gray-800">{operation.extendedProps.survey_ref}</p>
              </div>
            </div>
          )}

          {/* Client Information */}
          {parsed.client && (
            <div className="border-l-4 pl-4 py-2 rounded-r-lg" style={{ 
              borderLeftColor: borderColor,
              backgroundColor: `${bgColor}40`
            }}>
              <p className="text-sm text-kbl-gray-600 font-medium mb-1">Client</p>
              <p className="text-lg text-kbl-gray-800">{parsed.client}</p>
            </div>
          )}

          {/* Location Information */}
          {(parsed.port || parsed.country) && (
            <div className="border-l-4 pl-4 py-2 rounded-r-lg" style={{ 
              borderLeftColor: borderColor,
              backgroundColor: `${bgColor}40`
            }}>
              <p className="text-sm text-kbl-gray-600 font-medium mb-1">Location</p>
              <p className="text-lg text-kbl-gray-800">
                {parsed.port}
                {parsed.country && `, ${parsed.country}`}
              </p>
            </div>
          )}

          {/* Surveyor Information - Hide for clientusers */}
          {parsed.surveyor && !isClientuser && (
            <div className="border-l-4 pl-4 py-2 rounded-r-lg" style={{ 
              borderLeftColor: borderColor,
              backgroundColor: `${bgColor}40`
            }}>
              <p className="text-sm text-kbl-gray-600 font-medium mb-1">Surveyor</p>
              <p className="text-lg text-kbl-gray-800">{parsed.surveyor}</p>
            </div>
          )}

          {/* Notes - Hide for clientusers */}
          {parsed.notes && !isClientuser && (
            <div className="rounded-xl p-4" style={{ backgroundColor: `${bgColor}60` }}>
              <p className="text-sm text-kbl-gray-600 font-medium mb-2">Notes</p>
              <p className="text-kbl-gray-800 whitespace-pre-wrap leading-relaxed">{parsed.notes}</p>
            </div>
          )}

          {/* Survey Type */}
          {operation.extendedProps?.survey_type && (
            <div className="flex items-center gap-2">
              <span 
                className="inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold text-kbl-gray-700"
                style={{ backgroundColor: `${bgColor}80` }}
              >
                {operation.extendedProps.survey_type}
              </span>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="border-t border-kbl-gray-200 p-6 flex justify-end bg-kbl-gray-50">
          <button
            onClick={onClose}
            className="px-6 py-2.5 rounded-xl font-semibold transition-all duration-200 hover:scale-105 active:scale-95"
            style={{ 
              backgroundColor: borderColor,
              color: 'white'
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  )
}

// Custom Event Content Renderer with Neon Shine Effects
// Note: This component is used as a render function by FullCalendar
// We'll create a wrapper component that can use hooks properly
const EventContentWrapper = ({ event, isClientuser }) => {
  
  // Get country from extendedProps (parsed during event creation)
  const country = event.extendedProps?.country || ''
  const countryColor = getCountryColor(country)
  const bgColor = countryColor.bg
  const borderColor = countryColor.border
  
  // Parse description to extract information
  const description = event.extendedProps?.description || ''
  const parsed = parseDescription(description)
  
  // Extract vessel name from title (before the dash)
  const titleParts = event.title?.split('-') || []
  const fullVesselName = titleParts[0]?.trim() || event.title
  
  // Format vessel name (truncate to 12 characters)
  const truncateText = (text, maxLength) => {
    if (!text) return ''
    if (text.length <= maxLength) return text
    return text.substring(0, maxLength) + '..'
  }
  const displayVesselName = truncateText(fullVesselName, 12)
  
  // Format reference number (show only last 4 characters)
  const surveyRef = event.extendedProps?.survey_ref || ''
  const formattedRef = surveyRef ? '#' + surveyRef.slice(-4) : ''
  
  // Extract port name (before the comma if exists)
  const portName = parsed.port ? parsed.port.split(',')[0].trim() : ''
  
  // Format surveyor code (max 6 characters) - Hide for clientusers
  const formattedSurveyor = (!isClientuser && parsed.surveyor) ? parsed.surveyor.substring(0, 6) : ''

  return (
    <div 
      className="fc-event-main-frame event-card-neon relative overflow-hidden rounded-xl my-1"
      style={{ 
        borderLeftColor: borderColor,
        borderLeftWidth: '3px',
        backgroundColor: bgColor,
        border: 'none',
        borderTopLeftRadius: '0.75rem',
        borderBottomLeftRadius: '0.75rem',
        boxShadow: `0 4px 15px -3px ${borderColor}40, 0 2px 6px -2px ${borderColor}30`,
        position: 'relative',
      }}
    >
      {/* Neon glow effect on left border - reduced intensity */}
      <div 
        className="absolute left-0 top-1 bottom-1 w-1 opacity-60 blur-sm neon-glow rounded-full"
        style={{ 
          backgroundColor: borderColor,
          boxShadow: `0 0 4px ${borderColor}60, 0 0 8px ${borderColor}40`,
        }}
      />
      
      {/* Content - 2x2 grid layout matching backend */}
      <div className="relative z-10 p-2 w-full h-full flex flex-col justify-center">
        <div className="event-content w-full">
          {/* Row 1: Port (left) and Reference (right) */}
          <div className="event-row">
            <span className="event-port">{portName || ''}</span>
            <span className="event-ref">{formattedRef}</span>
          </div>
          {/* Row 2: Surveyor (left) and Vessel (right) - Hide surveyor for clientusers */}
          <div className="event-row">
            {isClientuser ? (
              <span className="event-vessel" style={{ width: '100%' }}>{displayVesselName}</span>
            ) : (
              <>
                <span className="event-surveyor">{formattedSurveyor}</span>
                <span className="event-vessel">{displayVesselName}</span>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// Wrapper to create EventContent with isClientuser from parent scope
const createEventContent = (isClientuser) => {
  return ({ event }) => <EventContentWrapper event={event} isClientuser={isClientuser} />
}

// Phones: the month grid columns are ~35px wide, too narrow for the 2x2 desktop card.
// The list view gets a full-width card; the month grid gets a one-line vessel chip.
const MobileEventContent = ({ event, view, isClientuser }) => {
  const countryColor = getCountryColor(event.extendedProps?.country || '')
  const parsed = parseDescription(event.extendedProps?.description || '')
  const vesselName = event.title?.split('-')[0]?.trim() || event.title
  const surveyRef = event.extendedProps?.survey_ref || ''

  if (view.type.startsWith('dayGrid')) {
    return (
      <div
        className="mobile-event-chip"
        style={{ backgroundColor: countryColor.bg, borderLeftColor: countryColor.border }}
      >
        {vesselName}
      </div>
    )
  }

  return (
    <div className="mobile-event-card" style={{ backgroundColor: countryColor.bg, borderLeftColor: countryColor.border }}>
      <div className="mobile-event-top">
        <span className="mobile-event-vessel">{vesselName}</span>
        {surveyRef && <span className="mobile-event-ref">#{surveyRef.slice(-4)}</span>}
      </div>
      <div className="mobile-event-meta">
        {parsed.port && <span>⚓ {parsed.port}</span>}
        {!isClientuser && parsed.surveyor && <span>👤 {parsed.surveyor}</span>}
      </div>
    </div>
  )
}

// Portrait phones by width; landscape phones are ~650-930px wide, so catch them as
// short touch screens (desktop browsers report a fine pointer)
const MOBILE_QUERY = '(max-width: 639px), (pointer: coarse) and (max-height: 500px)'

const OperationCalendar = () => {
  const { user } = useAuth()
  // Safely get isClientuser with fallback
  const isClientuser = user?.category === 'clientuser' || false
  const [events, setEvents] = useState([])
  const [allEvents, setAllEvents] = useState([]) // Store all events for filtering
  const [loading, setLoading] = useState(true)
  const [selectedOperation, setSelectedOperation] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedSurveyors, setSelectedSurveyors] = useState([])
  const [selectedCountries, setSelectedCountries] = useState([])
  const [selectedPorts, setSelectedPorts] = useState([])
  const [availableSurveyors, setAvailableSurveyors] = useState([])
  const [availableCountries, setAvailableCountries] = useState([])
  const [availablePorts, setAvailablePorts] = useState([])
  const [activeFilterField, setActiveFilterField] = useState('')
  const [showFilter, setShowFilter] = useState(false)
  const [isMobile, setIsMobile] = useState(() => window.matchMedia(MOBILE_QUERY).matches)
  const calendarRef = useRef(null)
  const isDraggingRef = useRef(false)
  const observerRef = useRef(null)
  const isReadOnly = !(user?.is_staff || user?.is_superuser)

  const fetchOperations = useCallback(async () => {
    try {
      const response = await operationAPI.list()
      console.log('Fetched operations raw data:', response.data.slice(0, 2)) // Debug first 2 operations
      const operations = response.data.map(op => {
        // Parse description to extract country
        const parsed = parseDescription(op.description || '')
        const country = parsed.country || ''
        
        // Get country-based color
        const countryColor = getCountryColor(country)
        
        // Parse date as European time (Europe/London) to ensure consistency
        // This ensures all dates are interpreted the same way regardless of user's location
        const parseEuropeanDate = (dateValue) => {
          if (!dateValue) return null
          
          // If it's already a string in YYYY-MM-DD format, return it as-is
          if (typeof dateValue === 'string') {
            // Extract YYYY-MM-DD from string
            const dateMatch = dateValue.match(/^(\d{4})-(\d{2})-(\d{2})/)
            if (dateMatch) {
              // Return the date string directly - it's already in the correct format
              return dateValue.substring(0, 10) // Ensure we only get YYYY-MM-DD part
            } else {
              // If it's an ISO datetime string, extract just the date part
              const datePart = dateValue.substring(0, 10)
              if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
                return datePart
              }
              // Fallback: parse as Date and format in Europe/London timezone
              const d = new Date(dateValue)
              const formatter = new Intl.DateTimeFormat('en-CA', {
                timeZone: 'Europe/London',
                year: 'numeric',
                month: '2-digit',
                day: '2-digit'
              })
              return formatter.format(d)
            }
          } else if (dateValue instanceof Date) {
            // For Date objects, extract date components in Europe/London timezone
            // This ensures consistency regardless of user's local timezone
            const formatter = new Intl.DateTimeFormat('en-CA', {
              timeZone: 'Europe/London',
              year: 'numeric',
              month: '2-digit',
              day: '2-digit'
            })
            return formatter.format(dateValue)
          } else {
            // Fallback: convert to Date and format in Europe/London timezone
            const d = new Date(dateValue)
            const formatter = new Intl.DateTimeFormat('en-CA', {
              timeZone: 'Europe/London',
              year: 'numeric',
              month: '2-digit',
              day: '2-digit'
            })
            return formatter.format(d)
          }
        }
        
        return {
          id: op.id.toString(),
          title: op.title,
          start: parseEuropeanDate(op.start_date),
          end: op.end_date ? parseEuropeanDate(op.end_date) : null,
          allDay: true, // Mark as all-day events - FullCalendar handles date strings correctly
          backgroundColor: countryColor.bg,
          borderColor: 'transparent', // We handle border in custom renderer
          extendedProps: {
            description: op.description,
            survey_ref: op.survey_ref,
            survey_type: op.survey_type,
            country: country, // Store country for consistent coloring
            color: op.color, // Keep original color for reference
          },
        }
      })
      setEvents(operations)
      setAllEvents(operations) // Store all events for filtering
      
      // Populate filter options from raw API data (before transformation)
      populateFilterOptions(response.data)
      
      // Log for debugging
      if (isClientuser) {
        console.log(`Clientuser operations fetched: ${operations.length} operations found`)
        if (operations.length === 0) {
          console.warn('No operations found for clientuser. Check backend filtering logic.')
        }
      }
    } catch (error) {
      console.error('Error fetching operations:', error)
      console.error('Error details:', error.response?.data || error.message)
    } finally {
      setLoading(false)
    }
  }, [isClientuser])

  useEffect(() => {
    const mql = window.matchMedia(MOBILE_QUERY)
    const onChange = (e) => setIsMobile(e.matches)
    mql.addEventListener('change', onChange)
    return () => mql.removeEventListener('change', onChange)
  }, [])

  useEffect(() => {
    fetchOperations()
  }, [fetchOperations])


  // Populate filter dropdown options from operations data
  const populateFilterOptions = (operationsData) => {
    const surveyors = new Set()
    const countries = new Set()
    const ports = new Set()
    
    operationsData.forEach(op => {
      // Get description from the raw operation data
      const description = op.description || ''
      const parsed = parseDescription(description)
      
      // Extract surveyors
      if (parsed.surveyor) {
        parsed.surveyor.split(',').forEach(s => {
          const trimmed = s.trim()
          if (trimmed && trimmed !== 'Not assigned') {
            surveyors.add(trimmed)
          }
        })
      }
      
      // Extract country and port
      if (parsed.country) {
        countries.add(parsed.country)
      }
      if (parsed.port) {
        ports.add(parsed.port)
      }
    })
    
    const surveyorsArray = Array.from(surveyors).sort()
    const countriesArray = Array.from(countries).sort()
    const portsArray = Array.from(ports).sort()
    
    console.log('Filter options populated:', {
      surveyors: surveyorsArray.length,
      countries: countriesArray.length,
      ports: portsArray.length
    })
    
    setAvailableSurveyors(surveyorsArray)
    setAvailableCountries(countriesArray)
    setAvailablePorts(portsArray)
  }

  // Filter events based on selected criteria
  const applyFilters = () => {
    if (selectedSurveyors.length === 0 && selectedCountries.length === 0 && selectedPorts.length === 0) {
      // No filters - show all events
      setEvents(allEvents)
      return
    }
    
    const filtered = allEvents.filter(event => {
      const parsed = parseDescription(event.extendedProps?.description || '')
      
      // Check surveyor match
      let matchesSurveyor = selectedSurveyors.length === 0
      if (selectedSurveyors.length > 0 && parsed.surveyor) {
        const eventSurveyors = parsed.surveyor.split(',').map(s => s.trim())
        matchesSurveyor = selectedSurveyors.some(selected => 
          eventSurveyors.includes(selected)
        )
      }
      
      // Check country match
      let matchesCountry = selectedCountries.length === 0
      if (selectedCountries.length > 0 && parsed.country) {
        matchesCountry = selectedCountries.includes(parsed.country)
      }
      
      // Check port match
      let matchesPort = selectedPorts.length === 0
      if (selectedPorts.length > 0 && parsed.port) {
        matchesPort = selectedPorts.includes(parsed.port)
      }
      
      return matchesSurveyor && matchesCountry && matchesPort
    })
    
    setEvents(filtered)
  }

  // Reset all filters
  const resetFilters = () => {
    setSelectedSurveyors([])
    setSelectedCountries([])
    setSelectedPorts([])
    setActiveFilterField('')
    setEvents(allEvents)
  }

  // Handle field selection change
  const handleFieldChange = (field) => {
    setActiveFilterField(field === activeFilterField ? '' : field)
  }

  // Handle option toggle for multi-select
  const toggleOption = (field, option) => {
    if (field === 'surveyor') {
      setSelectedSurveyors(prev => 
        prev.includes(option) 
          ? prev.filter(s => s !== option)
          : [...prev, option]
      )
    } else if (field === 'country') {
      setSelectedCountries(prev => 
        prev.includes(option) 
          ? prev.filter(c => c !== option)
          : [...prev, option]
      )
    } else if (field === 'port') {
      setSelectedPorts(prev => 
        prev.includes(option) 
          ? prev.filter(p => p !== option)
          : [...prev, option]
      )
    }
  }

  const handleEventClick = (info) => {
    setSelectedOperation(info.event)
    setIsModalOpen(true)
  }

  const closeModal = () => {
    setSelectedOperation(null)
    setIsModalOpen(false)
  }

  // Handle drag start - set custom drag image with correct offset for accurate cursor tracking
  const handleEventDragStart = (info) => {
    isDraggingRef.current = true
    const el = info.el
    const jsEvent = info.jsEvent
    
    if (el && jsEvent) {
      // Aggressively prevent FullCalendar from hiding the element
      // Use !important via setProperty to override any FullCalendar styles
      el.style.setProperty('opacity', '0.6', 'important')
      el.style.setProperty('visibility', 'visible', 'important')
      el.style.setProperty('display', 'block', 'important')
      el.style.setProperty('pointer-events', 'none', 'important')
      
      // Reset any hover effects that might interfere with drag positioning
      // Remove ALL transitions and animations immediately
      el.style.transform = 'none'
      el.style.transition = 'none'
      el.style.animation = 'none'
      
      // Also disable transitions on all child elements
      const children = el.querySelectorAll('*')
      children.forEach(child => {
        child.style.transition = 'none'
        child.style.animation = 'none'
      })
      
      // Calculate the offset from mouse click position to element's top-left corner
      // This ensures the drag preview follows the cursor exactly (1:1 movement)
      const rect = el.getBoundingClientRect()
      const offsetX = jsEvent.clientX - rect.left
      const offsetY = jsEvent.clientY - rect.top
      
      // Create a clone of the element to use as drag image
      // This ensures the drag preview is visible and matches the original element
      const dragImage = el.cloneNode(true)
      dragImage.style.position = 'absolute'
      dragImage.style.top = '-9999px'
      dragImage.style.left = '-9999px'
      dragImage.style.opacity = '0.95'
      dragImage.style.pointerEvents = 'none'
      dragImage.style.zIndex = '9999'
      dragImage.style.margin = '0'
      dragImage.style.padding = '0'
      // Ensure the clone has the same computed dimensions
      const computedStyle = window.getComputedStyle(el)
      dragImage.style.width = computedStyle.width
      dragImage.style.height = computedStyle.height
      dragImage.style.boxSizing = 'border-box'
      // Force visibility
      dragImage.style.visibility = 'visible'
      dragImage.style.display = 'block'
      document.body.appendChild(dragImage)
      
      // Force a reflow to ensure the clone is rendered before using it as drag image
      void dragImage.offsetHeight
      
      // Set custom drag image with calculated offset to fix cursor trajectory mismatch
      const nativeDragEvent = jsEvent.originalEvent || jsEvent
      if (nativeDragEvent && nativeDragEvent.dataTransfer) {
        try {
          // Use the cloned element as drag image with correct offset
          // This makes the ghost element follow the cursor exactly
          nativeDragEvent.dataTransfer.setDragImage(dragImage, offsetX, offsetY)
          
          // Clean up the clone after drag starts (browser takes ownership)
          setTimeout(() => {
            if (document.body.contains(dragImage)) {
              document.body.removeChild(dragImage)
            }
          }, 0)
        } catch (e) {
          // If setDragImage fails, clean up and fall back to default
          if (document.body.contains(dragImage)) {
            document.body.removeChild(dragImage)
          }
          console.debug('Could not set drag image in handleEventDragStart, using default')
        }
      } else {
        // Clean up if dataTransfer is not available
        if (document.body.contains(dragImage)) {
          document.body.removeChild(dragImage)
        }
      }
      
      // Use MutationObserver to prevent FullCalendar from hiding the element
      if (observerRef.current) {
        observerRef.current.disconnect()
      }
      
      observerRef.current = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
            // If opacity is being set to 0 or visibility hidden, override it
            const currentOpacity = el.style.opacity || window.getComputedStyle(el).opacity
            const currentVisibility = el.style.visibility || window.getComputedStyle(el).visibility
            const currentDisplay = el.style.display || window.getComputedStyle(el).display
            
            if (currentOpacity === '0' || currentVisibility === 'hidden' || currentDisplay === 'none') {
              el.style.setProperty('opacity', '0.6', 'important')
              el.style.setProperty('visibility', 'visible', 'important')
              el.style.setProperty('display', 'block', 'important')
            }
          }
        })
      })
      
      observerRef.current.observe(el, {
        attributes: true,
        attributeFilter: ['style', 'class'],
        childList: false,
        subtree: false
      })
    }
  }

  // Handle drag - keep original element visible
  const handleEventDrag = (info) => {
    const el = info.el
    if (el) {
      // Aggressively ensure the original element stays visible during drag
      el.style.setProperty('opacity', '0.6', 'important')
      el.style.setProperty('visibility', 'visible', 'important')
      el.style.setProperty('display', 'block', 'important')
      el.style.setProperty('pointer-events', 'none', 'important')
    }
  }

  // Handle drag end - restore transitions and visibility after drag completes
  const handleEventDragStop = (info) => {
    isDraggingRef.current = false
    
    // Clean up observer
    if (observerRef.current) {
      observerRef.current.disconnect()
      observerRef.current = null
    }
    
    const el = info.el
    if (el) {
      // Remove all the important styles we added
      el.style.removeProperty('opacity')
      el.style.removeProperty('visibility')
      el.style.removeProperty('display')
      el.style.removeProperty('pointer-events')
      // Restore transition for smooth hover effects
      el.style.transition = 'opacity 0.2s, box-shadow 0.2s'
    }
  }

  // Handle event mount - ensure proper positioning for drag operations
  const handleEventDidMount = (info) => {
    const el = info.el
    if (el && !isReadOnly) {
      // Ensure the event element is ready for drag operations
      // Set initial transition for hover effects
      el.style.transition = 'opacity 0.2s, box-shadow 0.2s'
      // Ensure no transforms that could interfere with position calculation
      el.style.transform = 'none'
      
      // Note: The drag image is set in handleEventDragStart which has access to
      // both the element and the click position for accurate offset calculation
    }
  }

  // Format date to YYYY-MM-DD (European timezone-safe)
  // For all-day events, FullCalendar provides dates that represent the date in the configured timezone
  // We need to extract the date components in Europe/London timezone
  const formatDate = (date) => {
    if (!date) return ''
    
    // If it's already a string in YYYY-MM-DD format, return it as-is
    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
      return date
    }
    
    // FullCalendar with timeZone="Europe/London" provides Date objects
    // For all-day events, we need to extract the date in Europe/London timezone
    const d = date instanceof Date ? date : new Date(date)
    
    // For all-day events in FullCalendar with timeZone="Europe/London",
    // the Date object represents midnight in Europe/London timezone
    // We need to extract the date components as they appear in Europe/London
    // Use Intl.DateTimeFormat which is more reliable for timezone conversion
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Europe/London',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    })
    
    // Format returns YYYY-MM-DD directly for en-CA locale
    return formatter.format(d)
  }

  // Handle event drop (when moved to a new date)
  const handleEventDrop = async (info) => {
    if (isReadOnly) {
      info.revert()
      return
    }

    if (!window.confirm('Are you sure you want to move this event?')) {
      info.revert()
      return
    }

    // Store the original position for smooth transition
    const event = info.event
    const originalStart = event.start
    const originalEnd = event.end
    
    try {
      // For all-day events, FullCalendar provides the date in the configured timezone
      // We need to extract it correctly to avoid timezone conversion issues
      let startDate, endDate
      
      // Extract date from event
      // For all-day events, FullCalendar should provide startStr/endStr (timezone-safe YYYY-MM-DD strings)
      // If not available, we extract from the Date object in Europe/London timezone
      
      // For all-day events, FullCalendar provides startStr/endStr which are timezone-safe YYYY-MM-DD strings
      // Always prefer startStr for all-day events to avoid timezone conversion issues
      if (event.allDay && event.startStr && /^\d{4}-\d{2}-\d{2}$/.test(event.startStr)) {
        // Use startStr directly - it's already in the correct format and timezone-safe
        startDate = event.startStr
        endDate = (event.endStr && /^\d{4}-\d{2}-\d{2}$/.test(event.endStr)) ? event.endStr : startDate
      } else if (event.startStr && /^\d{4}-\d{2}-\d{2}$/.test(event.startStr)) {
        // Even if not explicitly allDay, if startStr is available, use it
        startDate = event.startStr
        endDate = (event.endStr && /^\d{4}-\d{2}-\d{2}$/.test(event.endStr)) ? event.endStr : startDate
      } else {
        // Fallback: extract from Date object in Europe/London timezone
        // This should rarely happen for all-day events, but handle it gracefully
        startDate = formatDate(event.start)
        endDate = event.end ? formatDate(event.end) : startDate
        
        // Debug: log if startStr is not available
        console.warn('startStr not available for event, using formatDate fallback:', {
          eventId: event.id,
          allDay: event.allDay,
          start: event.start,
          formatted: startDate,
          startStr: event.startStr
        })
      }

      const data = {
        id: parseInt(event.id),
        title: event.title,
        start: startDate,
        end: endDate,
        description: event.extendedProps?.description || '',
        color: event.extendedProps?.color || 'orange',
        survey_ref: event.extendedProps?.survey_ref || null,
        surveyType: event.extendedProps?.survey_type || '',
        update_eta: true,
      }

      // Debug logging to track date flow
      console.log('Sending operation update:', {
        eventId: event.id,
        startDate: startDate,
        endDate: endDate,
        eventStart: event.start,
        eventStartStr: event.startStr,
        eventAllDay: event.allDay,
        surveyRef: event.extendedProps?.survey_ref
      })

      const response = await operationAPI.save(data)
      
      if (response.data?.status === 'success') {
        // Don't refresh all events - let FullCalendar handle the transition animation
        // The event is already in its new position visually, we just need to ensure
        // the data is synced. Only refresh if there's a mismatch.
        // This allows the smooth transition from initial to final position.
      } else {
        throw new Error(response.data?.message || 'Failed to update event')
      }
    } catch (error) {
      console.error('Error updating event:', error)
      alert('Error updating event: ' + (error.response?.data?.message || error.message))
      // Revert to original position on error
      event.setStart(originalStart)
      if (originalEnd) {
        event.setEnd(originalEnd)
      }
      info.revert()
    }
  }

  // Handle event resize (when duration is changed)
  const handleEventResize = async (info) => {
    if (isReadOnly) {
      info.revert()
      return
    }

    if (!window.confirm('Are you sure you want to change the event duration?')) {
      info.revert()
      return
    }

    // Store the original position for smooth transition
    const event = info.event
    const originalStart = event.start
    const originalEnd = event.end

    try {
      // For all-day events, use startStr/endStr if available (timezone-safe)
      let startDate, endDate
      
      // Always prefer startStr for all-day events to avoid timezone conversion issues
      if (event.allDay && event.startStr && /^\d{4}-\d{2}-\d{2}$/.test(event.startStr)) {
        startDate = event.startStr
        endDate = (event.endStr && /^\d{4}-\d{2}-\d{2}$/.test(event.endStr)) ? event.endStr : startDate
      } else if (event.startStr && /^\d{4}-\d{2}-\d{2}$/.test(event.startStr)) {
        // Even if not explicitly allDay, if startStr is available, use it
        startDate = event.startStr
        endDate = (event.endStr && /^\d{4}-\d{2}-\d{2}$/.test(event.endStr)) ? event.endStr : startDate
      } else {
        // Fallback: format the Date object in Europe/London timezone
        startDate = formatDate(event.start)
        endDate = event.end ? formatDate(event.end) : startDate
      }

      const data = {
        id: parseInt(event.id),
        title: event.title,
        start: startDate,
        end: endDate,
        description: event.extendedProps?.description || '',
        color: event.extendedProps?.color || 'orange',
        survey_ref: event.extendedProps?.survey_ref || null,
        surveyType: event.extendedProps?.survey_type || '',
        update_eta: true,
      }

      const response = await operationAPI.save(data)
      
      if (response.data?.status === 'success') {
        // Don't refresh all events - let FullCalendar handle the transition animation
        // The event is already in its new position visually, we just need to ensure
        // the data is synced. This allows the smooth transition from initial to final position.
      } else {
        throw new Error(response.data?.message || 'Failed to update event')
      }
    } catch (error) {
      console.error('Error updating event:', error)
      alert('Error updating event: ' + (error.response?.data?.message || error.message))
      // Revert to original position on error
      event.setStart(originalStart)
      if (originalEnd) {
        event.setEnd(originalEnd)
      }
      info.revert()
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#ea580c]"></div>
      </div>
    )
  }

  return (
    <>
    <div className="page-container">
      <PageHeader title="KBL Operations Calendar" description="Schedule and track vessel operations">
        <button
          onClick={() => setShowFilter(!showFilter)}
          className={`p-2 rounded-lg border transition-colors ${showFilter ? 'border-[#ea580c] bg-[#fff7ed] text-[#c2410c]' : 'border-[#efe3d6] bg-white text-[#c2410c] hover:border-[#ea580c] hover:bg-[#fff7ed]'}`}
          title="Toggle Filters"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
          </svg>
        </button>
      </PageHeader>

      {/* Filter Controls - Similar to Dashboard */}
      {showFilter && (
      <div className="bg-white rounded-xl border border-[#efe3d6] shadow-[0_1px_6px_rgba(234,88,12,0.12)] overflow-hidden mb-6">
        <div className="table-head-premium px-5 py-2">
          <h3 className="text-sm font-semibold flex items-center justify-between text-white">
            <div className="flex items-center gap-2">
              <span className="shrink-0 text-white" aria-hidden="true">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
              </span>
              <span>Filter Options</span>
            </div>
            <button
              onClick={() => setShowFilter(false)}
              className="p-1.5 rounded-lg hover:bg-white/20 text-white transition-all duration-200 hover:scale-110 active:scale-95"
              title="Close Filters"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </h3>
        </div>
        <div className="p-5">
        <div className="flex flex-col sm:flex-row gap-3 items-start">
          {/* Filter Field Selector */}
          <div className="w-full sm:w-[220px] flex-shrink-0">
            <select
              value={activeFilterField}
              onChange={(e) => handleFieldChange(e.target.value)}
              className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-[#ea580c] bg-white text-gray-800 transition-colors"
            >
              <option value="">🔍 Select Filter Field</option>
              {!isClientuser && <option value="surveyor">👤 Surveyor</option>}
              <option value="country">🌍 Country</option>
              <option value="port">⚓ Port</option>
            </select>
          </div>

          {/* Filter Options Dropdown - Shows when field is selected */}
          {activeFilterField && (
            <div className="flex-1 relative">
              <div className="border border-[#efe3d6] rounded-lg bg-white shadow-sm p-3 max-h-[300px] overflow-y-auto">
                <div className="flex items-center justify-between mb-2 pb-2 border-b border-[#efe3d6]">
                  <span className="text-sm font-semibold text-[#9a3412]">
                    Select {activeFilterField === 'surveyor' ? 'Surveyors' : activeFilterField === 'country' ? 'Countries' : 'Ports'} (Multiple)
                  </span>
                  <button
                    onClick={() => setActiveFilterField('')}
                    className="text-kbl-gray-400 hover:text-kbl-gray-600 transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
                <div className="space-y-1">
                  {activeFilterField === 'surveyor' && availableSurveyors.length > 0 ? (
                    availableSurveyors.map(surveyor => (
                      <label key={surveyor} className="flex items-center gap-2 p-2 hover:bg-orange-50 rounded-lg cursor-pointer transition-colors">
                        <input
                          type="checkbox"
                          checked={selectedSurveyors.includes(surveyor)}
                          onChange={() => toggleOption('surveyor', surveyor)}
                          className="w-4 h-4 text-orange-500 border-orange-300 rounded focus:ring-orange-500"
                        />
                        <span className="text-sm text-kbl-gray-700">{surveyor}</span>
                      </label>
                    ))
                  ) : activeFilterField === 'surveyor' ? (
                    <p className="text-sm text-kbl-gray-500 py-2">No surveyors available</p>
                  ) : null}
                  {activeFilterField === 'country' && availableCountries.length > 0 ? (
                    availableCountries.map(country => (
                      <label key={country} className="flex items-center gap-2 p-2 hover:bg-orange-50 rounded-lg cursor-pointer transition-colors">
                        <input
                          type="checkbox"
                          checked={selectedCountries.includes(country)}
                          onChange={() => toggleOption('country', country)}
                          className="w-4 h-4 text-orange-500 border-orange-300 rounded focus:ring-orange-500"
                        />
                        <span className="text-sm text-kbl-gray-700">{country}</span>
                      </label>
                    ))
                  ) : activeFilterField === 'country' ? (
                    <p className="text-sm text-kbl-gray-500 py-2">No countries available</p>
                  ) : null}
                  {activeFilterField === 'port' && availablePorts.length > 0 ? (
                    availablePorts.map(port => (
                      <label key={port} className="flex items-center gap-2 p-2 hover:bg-orange-50 rounded-lg cursor-pointer transition-colors">
                        <input
                          type="checkbox"
                          checked={selectedPorts.includes(port)}
                          onChange={() => toggleOption('port', port)}
                          className="w-4 h-4 text-orange-500 border-orange-300 rounded focus:ring-orange-500"
                        />
                        <span className="text-sm text-kbl-gray-700">{port}</span>
                      </label>
                    ))
                  ) : activeFilterField === 'port' ? (
                    <p className="text-sm text-kbl-gray-500 py-2">No ports available</p>
                  ) : null}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer: Selected Filters (Left) and Action Buttons (Right) */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-4 mt-4 border-t border-gray-200">
          {/* Selected Filters - Left side */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-semibold text-gray-700 whitespace-nowrap">Selected Filters:</span>
              <div className="flex items-center gap-2 flex-wrap">
                {selectedSurveyors.map(surveyor => (
                  <span key={surveyor} className="inline-flex items-center gap-1 px-2 py-1 bg-[#fff7ed] text-[#9a3412] border border-[#fed7aa] rounded-full text-xs font-medium whitespace-nowrap">
                    👤 {surveyor}
                    <button
                      type="button"
                      onClick={() => toggleOption('surveyor', surveyor)}
                      className="hover:text-[#c2410c]"
                    >
                      ×
                    </button>
                  </span>
                ))}
                {selectedCountries.map(country => (
                  <span key={country} className="inline-flex items-center gap-1 px-2 py-1 bg-[#fff7ed] text-[#9a3412] border border-[#fed7aa] rounded-full text-xs font-medium whitespace-nowrap">
                    🌍 {country}
                    <button
                      type="button"
                      onClick={() => toggleOption('country', country)}
                      className="hover:text-[#c2410c]"
                    >
                      ×
                    </button>
                  </span>
                ))}
                {selectedPorts.map(port => (
                  <span key={port} className="inline-flex items-center gap-1 px-2 py-1 bg-[#fff7ed] text-[#9a3412] border border-[#fed7aa] rounded-full text-xs font-medium whitespace-nowrap">
                    ⚓ {port}
                    <button
                      type="button"
                      onClick={() => toggleOption('port', port)}
                      className="hover:text-[#c2410c]"
                    >
                      ×
                    </button>
                  </span>
                ))}
                {selectedSurveyors.length === 0 && selectedCountries.length === 0 && selectedPorts.length === 0 && (
                  <span className="text-xs text-gray-400 italic">No filters selected</span>
                )}
              </div>
            </div>
          </div>

          {/* Action Buttons - Right end */}
          <div className="flex gap-3 flex-shrink-0">
            <button
              type="button"
              onClick={resetFilters}
              disabled={selectedSurveyors.length === 0 && selectedCountries.length === 0 && selectedPorts.length === 0}
              className="px-4 py-2 text-sm bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 font-semibold rounded-lg transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              Clear All
            </button>
            <button
              type="button"
              onClick={applyFilters}
              className="px-4 py-2 text-sm bg-premium text-white font-semibold rounded-lg shadow-[0_2px_8px_rgba(234,88,12,0.3)] hover:shadow-[0_4px_14px_rgba(234,88,12,0.4)] transition-shadow flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Apply Filters
            </button>
          </div>
        </div>
        </div>
      </div>
      )}
      
      {events.length === 0 && !loading && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
          <div className="flex items-start gap-2">
            <i className="bi bi-info-circle text-yellow-600 mt-1"></i>
            <div className="flex-1">
              <p className="text-yellow-800 font-semibold mb-1">
                {isClientuser 
                  ? 'No operations found for your client account.'
                  : 'No operations found.'}
              </p>
              <p className="text-yellow-700 text-sm">
                {isClientuser 
                  ? 'Operations will appear here once surveys are added to the calendar for your associated client(s). If you believe this is an error, please contact your administrator to verify that your user account is properly linked to client records in the database.'
                  : 'Operations will appear here once surveys are added to the calendar.'}
              </p>
              {isClientuser && (
                <p className="text-yellow-600 text-xs mt-2 font-mono">
                  Debug: User: {user?.username}, Category: {user?.category || 'Not set'}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
      
      <div className={`card-premium ${isMobile ? 'p-2 operation-calendar-mobile' : 'p-4 md:p-5'}`}>
        <FullCalendar
          // Remount when crossing the phone breakpoint so initialView/toolbar take effect
          key={isMobile ? 'mobile' : 'desktop'}
          ref={calendarRef}
          plugins={[dayGridPlugin, interactionPlugin, listPlugin]}
          initialView={isMobile ? 'listMonth' : 'dayGridMonth'}
          events={events}
          height="auto"
          timeZone="Europe/London"
          headerToolbar={isMobile ? {
            left: 'prev,next',
            center: 'title',
            right: 'listMonth,dayGridMonth',
          } : {
            left: 'prev,next today',
            center: 'title',
            right: 'dayGridMonth,dayGridWeek',
          }}
          buttonText={isMobile ? { listMonth: 'List', dayGridMonth: 'Month' } : undefined}
          noEventsText="No operations this month"
          // Single short day label ("Sun, Oct 11") so the list header fits a phone-width card
          listDayFormat={{ weekday: 'short', month: 'short', day: 'numeric' }}
          listDaySideFormat={false}
          // No drag & drop on phones: touch scrolling would move operations by accident
          editable={!isReadOnly && !isMobile}
          eventDrop={handleEventDrop}
          eventResize={handleEventResize}
          eventClick={handleEventClick}
          eventContent={isMobile
            ? (arg) => <MobileEventContent event={arg.event} view={arg.view} isClientuser={isClientuser} />
            : createEventContent(isClientuser)}
          eventDidMount={handleEventDidMount}
          eventDragStart={handleEventDragStart}
          eventDrag={handleEventDrag}
          eventDragStop={handleEventDragStop}
          dayMaxEvents={isMobile ? 2 : 3}
          moreLinkContent={isMobile ? (arg) => `+${arg.num}` : undefined}
          moreLinkClick="popover"
          eventDisplay="block"
          eventTextColor="#1f2937"
          eventBorderColor="transparent"
          eventClassNames={isReadOnly || isMobile ? "cursor-pointer fc-event-read-only" : "cursor-move"}
          // Ensure smooth transitions during drag operations
          eventStartEditable={!isReadOnly && !isMobile}
          eventDurationEditable={!isReadOnly && !isMobile}
          eventMouseEnter={(info) => {
            // Only apply hover effect if not currently dragging
            // Use opacity and box-shadow instead of transform to avoid position calculation issues
            if (!isDraggingRef.current) {
              const el = info.el
              el.style.opacity = '0.95'
              el.style.transition = 'opacity 0.2s, box-shadow 0.2s'
              // Enhance box-shadow for hover effect
              const currentBoxShadow = window.getComputedStyle(el).boxShadow
              if (!currentBoxShadow || currentBoxShadow === 'none') {
                el.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)'
              }
            }
          }}
          eventMouseLeave={(info) => {
            // Reset hover effect if not currently dragging
            if (!isDraggingRef.current) {
              const el = info.el
              el.style.opacity = '1'
              el.style.boxShadow = ''
            }
          }}
        />
      </div>

      <OperationModal
        operation={selectedOperation}
        isOpen={isModalOpen}
        onClose={closeModal}
      />
    </div>
    </>
  )
}

export default OperationCalendar


